import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Network,
  Plus,
  AlertCircle,
  Clock,
  MoreHorizontal,
  Trash2,
  Edit3,
  ExternalLink,
  Copy,
  BrainCircuit,
  Star,
  Zap,
  Map as MapIcon,
  GitBranch,
  Target,
  Lightbulb,
} from 'lucide-react';
import { docsApi, type Doc } from '../api/docs';
import type { Member } from '../api/members';
import { usersApi } from '../api/users';
import { toast, useToastStore } from '../components/Toast';
import { formatRelativeTime, getInitials, getCollaboratorColor } from '../lib/utils';

interface ContextMenuState {
  docId: string;
  x: number;
  y: number;
}

const MAP_ICON_CHOICES = ['Network', 'Map', 'GitBranch', 'Target', 'Lightbulb', 'BrainCircuit', 'Star', 'Zap'];

export function MindmapListPage() {
  const { wsId = '' } = useParams<{ wsId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: mindmaps,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['mindmaps', wsId],
    queryFn: () => docsApi.list(wsId, { type: 'mindmap' }),
    enabled: !!wsId,
  });

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: usersApi.me,
  });

  const { data: members } = useQuery({
    queryKey: ['members', wsId],
    queryFn: () => import('../api/members').then((m) => m.membersApi.list(wsId)),
    enabled: !!wsId,
  });

  // 创建弹窗
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState(MAP_ICON_CHOICES[0]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // 右键菜单
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  // 删除确认弹窗
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState<Doc | null>(null);

  // 重命名弹窗
  const [renameDoc, setRenameDoc] = useState<Doc | null>(null);
  const [renameTitle, setRenameTitle] = useState('');

  const createMutation = useMutation({
    mutationFn: () =>
      docsApi.create(wsId, {
        title: title.trim() || '未命名导图',
        emoji: icon,
        type: 'mindmap',
      }),
    onSuccess: (doc) => {
      queryClient.invalidateQueries({ queryKey: ['mindmaps', wsId] });
      toast.success('思维导图已创建');
      setOpen(false);
      setTitle('');
      setIcon(MAP_ICON_CHOICES[0]);
      setSubmitError(null);
      navigate(`/space/${wsId}/mindmaps/${doc.id}`);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '创建失败';
      setSubmitError(msg);
      toast.error(msg, '创建失败');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (docId: string) => docsApi.remove(docId),
    onSuccess: (_data, docId) => {
      queryClient.invalidateQueries({ queryKey: ['mindmaps', wsId] });
      setDeleteConfirmDoc(null);
      const restoredDocId = docId;
      useToastStore.getState().push({
        description: '已删除',
        variant: 'success',
        duration: 5000,
        action: {
          label: '撤销',
          onClick: () => {
            docsApi.restore(wsId, restoredDocId).then(() => {
              queryClient.invalidateQueries({ queryKey: ['mindmaps', wsId] });
              toast.success('已恢复');
            });
          },
        },
      });
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '删除失败';
      toast.error(msg, '删除失败');
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ docId, title }: { docId: string; title: string }) =>
      docsApi.update(docId, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mindmaps', wsId] });
      setRenameDoc(null);
      toast.success('已重命名');
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : '重命名失败';
      toast.error(msg, '重命名失败');
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate();
  }

  function handleContextMenu(e: React.MouseEvent, docId: string) {
    e.preventDefault();
    e.stopPropagation();
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 180);
    setContextMenu({ docId, x, y });
  }

  function closeContextMenu() {
    setContextMenu(null);
  }

  function handleOpenDoc(doc: Doc) {
    closeContextMenu();
    navigate(`/space/${wsId}/mindmaps/${doc.id}`);
  }

  function handleRenameStart(doc: Doc) {
    closeContextMenu();
    setRenameTitle(doc.title || '');
    setRenameDoc(doc);
  }

  function handleRenameSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!renameDoc) return;
    const newTitle = renameTitle.trim();
    if (!newTitle) return;
    if (newTitle === renameDoc.title) {
      setRenameDoc(null);
      return;
    }
    renameMutation.mutate({ docId: renameDoc.id, title: newTitle });
  }

  function handleDeleteConfirm(doc: Doc) {
    closeContextMenu();
    setDeleteConfirmDoc(doc);
  }

  function handleDeleteExecute() {
    if (!deleteConfirmDoc) return;
    deleteMutation.mutate(deleteConfirmDoc.id);
  }

  function handleCopyLink(doc: Doc) {
    closeContextMenu();
    const targetPath = `/space/${wsId}/mindmaps/${doc.id}`;
    navigator.clipboard.writeText(`${window.location.origin}${targetPath}`).then(
      () => toast.success('链接已复制'),
      () => toast.error('复制失败'),
    );
  }

  // 点击外部关闭右键菜单
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        closeContextMenu();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') closeContextMenu();
    }
    if (contextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [contextMenu]);

  const memberMap = new globalThis.Map<string, Member>();
  const membersArr = (members as Member[] | undefined) ?? [];
  membersArr.forEach((m) => memberMap.set(m.id, m));

  return (
    <div className="mx-auto max-w-5xl px-8 py-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#1a1a2e]">思维导图</h1>
          <p className="mt-1 text-sm text-text-secondary">
            {mindmaps ? `${mindmaps.length} 个导图` : '空间内的所有思维导图'}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-md"
          onClick={() => {
            setTitle('');
            setIcon(MAP_ICON_CHOICES[0]);
            setSubmitError(null);
            setOpen(true);
          }}
        >
          <Plus className="h-4 w-4" /> 新建导图
        </button>
      </div>

      {isLoading ? (
        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="card flex flex-col gap-3 p-4">
              <div className="flex items-start gap-3">
                <div className="h-12 w-12 shrink-0 animate-shimmer rounded-xl bg-bg-subtle" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 animate-shimmer rounded bg-bg-subtle" />
                  <div className="h-3 w-1/3 animate-shimmer rounded bg-bg-subtle" />
                </div>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-2">
                <div className="h-3 w-20 animate-shimmer rounded bg-bg-subtle" />
                <div className="h-3 w-12 animate-shimmer rounded bg-bg-subtle" />
              </div>
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="mt-8 flex flex-col items-center rounded-xl bg-bg-surface p-10 text-center ring-1 ring-black/5 shadow-sm">
          <AlertCircle className="h-8 w-8 text-danger" />
          <p className="mt-3 text-sm text-text-secondary">
            {error instanceof Error ? error.message : '加载失败'}
          </p>
          <button
            type="button"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['mindmaps', wsId] })}
            className="btn btn-secondary btn-md mt-4"
          >
            重试
          </button>
        </div>
      ) : mindmaps && mindmaps.length > 0 ? (
        <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {mindmaps.map((doc) => {
            const editor = doc.lastEditedBy ? memberMap.get(doc.lastEditedBy) : undefined;
            const editorName =
              editor?.nickname ||
              editor?.email?.split('@')[0] ||
              (doc.lastEditedBy === me?.id ? '我' : '未知');
            const isMe = doc.lastEditedBy && doc.lastEditedBy === me?.id;
            return (
              <div
                key={doc.id}
                className="card group relative flex flex-col gap-3 p-5 text-left"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/space/${wsId}/mindmaps/${doc.id}`)}
                  onContextMenu={(e) => handleContextMenu(e, doc.id)}
                  className="flex flex-1 flex-col gap-3 text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-accent-50 to-accent-100 shadow-sm ring-1 ring-accent/20">
                      {doc.emoji ? (
                        <span className="text-xl">{doc.emoji}</span>
                      ) : (
                        <Network className="h-5 w-5 text-accent" />
                      )}
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <div className="truncate text-sm font-medium text-[#1a1a2e]">{doc.title || '未命名导图'}</div>
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-text-tertiary">
                        <Network className="h-3 w-3" />
                        思维导图
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between border-t border-border pt-2 text-xs text-text-tertiary">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatRelativeTime(doc.updatedAt)}
                    </span>
                    {editor && (
                      <span className="flex items-center gap-1">
                        <span
                          className="flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-medium text-white"
                          style={{
                            backgroundColor: getCollaboratorColor(
                              doc.lastEditedBy ?? editor.id,
                            ),
                          }}
                          aria-hidden
                        >
                          {getInitials(isMe ? '我' : editorName)}
                        </span>
                        {isMe ? '我' : editorName}
                      </span>
                    )}
                  </div>
                </button>
                {/* 操作按钮（hover 显示） */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleContextMenu(e as unknown as React.MouseEvent, doc.id);
                  }}
                  className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md text-text-tertiary opacity-0 transition-opacity hover:bg-bg-subtle hover:text-text-primary group-hover:opacity-100"
                  aria-label="更多操作"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-12 flex flex-col items-center text-center">
          <svg
            width="120"
            height="120"
            viewBox="0 0 120 120"
            fill="none"
            className="mb-4"
            aria-hidden="true"
          >
            <circle cx="60" cy="40" r="16" className="fill-accent-100" />
            <circle cx="30" cy="80" r="12" className="fill-accent-50" />
            <circle cx="90" cy="80" r="12" className="fill-accent-50" />
            <line x1="44" y1="52" x2="36" y2="70" className="stroke-accent-200" strokeWidth="2" />
            <line x1="76" y1="52" x2="84" y2="70" className="stroke-accent-200" strokeWidth="2" />
            <line x1="42" y1="80" x2="78" y2="80" className="stroke-accent-200" strokeWidth="2" />
            <circle cx="60" cy="40" r="16" className="fill-accent" />
            <path
              d="M55 38l4 4 6-7"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <p className="text-base font-medium text-[#1a1a2e]">还没有任何思维导图</p>
          <p className="mt-1 text-sm text-text-secondary">创建你的第一个思维导图，开启头脑风暴</p>
          <button
            type="button"
            className="btn btn-primary btn-md mt-4"
            onClick={() => {
              setTitle('');
              setIcon(MAP_ICON_CHOICES[0]);
              setSubmitError(null);
              setOpen(true);
            }}
          >
            <Plus className="h-4 w-4" /> 新建思维导图
          </button>
        </div>
      )}

      {/* 右键菜单 */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed z-50 w-44 rounded-lg border border-border bg-bg-surface py-1 shadow-lg"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-bg-subtle"
            onClick={() => {
              const doc = mindmaps?.find((d) => d.id === contextMenu.docId);
              if (doc) handleOpenDoc(doc);
            }}
          >
            <ExternalLink className="h-4 w-4" />
            打开
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-bg-subtle"
            onClick={() => {
              const doc = mindmaps?.find((d) => d.id === contextMenu.docId);
              if (doc) handleRenameStart(doc);
            }}
          >
            <Edit3 className="h-4 w-4" />
            重命名
          </button>
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary transition-colors hover:bg-bg-subtle"
            onClick={() => {
              const doc = mindmaps?.find((d) => d.id === contextMenu.docId);
              if (doc) handleCopyLink(doc);
            }}
          >
            <Copy className="h-4 w-4" />
            复制链接
          </button>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger transition-colors hover:bg-[#fef2f2]"
            onClick={() => {
              const doc = mindmaps?.find((d) => d.id === contextMenu.docId);
              if (doc) handleDeleteConfirm(doc);
            }}
          >
            <Trash2 className="h-4 w-4" />
            删除
          </button>
        </div>
      )}

      {/* 创建弹窗 */}
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
            aria-describedby={undefined}
          >
            <Dialog.Title className="text-lg font-semibold tracking-tight text-[#1a1a2e]">新建思维导图</Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-text-secondary">
              创建后会进入思维导图编辑器。
            </Dialog.Description>

            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="map-title">
                  标题
                </label>
                <input
                  id="map-title"
                  type="text"
                  maxLength={200}
                  autoFocus
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input"
                  placeholder="未命名导图"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">图标</label>
                <div className="flex flex-wrap gap-2">
                  {MAP_ICON_CHOICES.map((iconName) => {
                    const IconComponent = iconName === 'Network' ? Network :
                      iconName === 'Map' ? MapIcon :
                      iconName === 'GitBranch' ? GitBranch :
                      iconName === 'Target' ? Target :
                      iconName === 'Lightbulb' ? Lightbulb :
                      iconName === 'BrainCircuit' ? BrainCircuit :
                      iconName === 'Star' ? Star :
                      iconName === 'Zap' ? Zap : Network;
                    return (
                      <button
                        key={iconName}
                        type="button"
                        onClick={() => setIcon(iconName)}
                        className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${
                          icon === iconName
                            ? 'border-accent bg-accent-50 text-accent'
                            : 'border-border text-text-secondary hover:bg-bg-subtle hover:text-text-primary'
                        }`}
                        aria-pressed={icon === iconName}
                        aria-label={`选择图标 ${iconName}`}
                      >
                        <IconComponent className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>
              </div>

              {submitError && (
                <div className="flex items-center gap-2 rounded-md bg-[#fef2f2] px-3 py-2 text-sm text-danger">
                  <AlertCircle className="h-4 w-4" />
                  {submitError}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="btn btn-ghost btn-md">
                  取消
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn btn-primary btn-md"
                >
                  {createMutation.isPending ? '创建中...' : '创建'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 删除确认弹窗 */}
      <Dialog.Root
        open={!!deleteConfirmDoc}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmDoc(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[420px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
            aria-describedby={undefined}
          >
            <Dialog.Title className="text-lg font-semibold">确认删除</Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-text-secondary">
              确定要删除「{deleteConfirmDoc?.title || '未命名导图'}」吗？删除后可以
              <span className="font-medium">撤销</span>恢复。
            </Dialog.Description>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmDoc(null)}
                className="btn btn-ghost btn-md"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleDeleteExecute}
                disabled={deleteMutation.isPending}
                className="btn btn-danger btn-md"
              >
                {deleteMutation.isPending ? '删除中...' : '删除'}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* 重命名弹窗 */}
      <Dialog.Root
        open={!!renameDoc}
        onOpenChange={(open) => {
          if (!open) setRenameDoc(null);
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-bg-overlay backdrop-blur-sm animate-fade-in" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-bg-surface p-6 shadow-xl ring-1 ring-black/5"
            aria-describedby={undefined}
          >
            <Dialog.Title className="text-lg font-semibold">重命名</Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-text-secondary">
              输入新的标题
            </Dialog.Description>

            <form onSubmit={handleRenameSubmit} className="mt-5">
              <input
                type="text"
                maxLength={200}
                autoFocus
                value={renameTitle}
                onChange={(e) => setRenameTitle(e.target.value)}
                className="input"
                placeholder="输入标题"
              />
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setRenameDoc(null)}
                  className="btn btn-ghost btn-md"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={renameMutation.isPending || !renameTitle.trim()}
                  className="btn btn-primary btn-md"
                >
                  {renameMutation.isPending ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}